import React, { memo } from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';

import fontService, { FontWeight, TextType } from 'services/FontService';
import { tablet } from 'services/ui';
import { Colors } from 'context';
import { Button, ButtonSizes, Icon, IconNames } from 'atoms';
import { parseDateOnly } from 'services/DateService';

/** Interaction state as it arrives on the wire: 'saved' | 'applied' | null. */
export type CareerVacancyInteraction = 'saved' | 'applied' | null;

export interface ICareerVacancyCard {
  title: string;
  companyName: string;
  location: string;
  /** Raw wire enum values — the card resolves labels via i18n and hides blanks. */
  workMode: string;
  seniority: string;
  employmentType: string;
  /** Pre-formatted salary range; empty string hides the row. */
  salary: string;
  description: string;
  directionTitles: string[];
  skillNames: string[];
  applyUrl: string;
  /** ISO date; empty hides the row. */
  expiresAt: string;
  interaction: CareerVacancyInteraction;
  onSave: () => void;
  /** Открывает подробный вид — описание целиком и все атрибуты. */
  onOpenDetails: () => void;
  actionLoading: boolean;
}

const MAX_SKILL_CHIPS = 8;

/**
 * Одна вакансия в ленте (ТЗ 0018 §B2, переделана после ручной проверки).
 *
 * Что изменилось и почему:
 *
 * * ГЛАВНОЕ действие — «Посмотреть вакансию» на внешнюю ссылку. Раньше это была
 *   мелкая текстовая ссылка в углу, а низ карточки занимали две одинаковые
 *   кнопки «Zapisz» и «Aplikuję», которые НИЧЕГО не отправляли работодателю:
 *   первая клала строку в избранное, вторая — самоотметку «я где-то откликнулся».
 *   Пользователю они читались как два способа откликнуться;
 * * кнопка «Aplikuję» убрана совсем (решение владельца), сохранение осталось
 *   иконкой-переключателем — теперь его можно снять;
 * * у каждого атрибута своя иконка. Карточки выглядели одинаково не только
 *   из-за оформления: обязательны лишь заголовок и ссылка, поэтому у большинства
 *   вакансий заполнены два поля из семи, а остальные блоки просто не рисуются.
 *   Иконка + подпись показывают, ЧТО именно заполнено;
 * * клик по карточке открывает подробный вид: описание здесь обрезано тремя
 *   строками, и до правки прочитать его целиком было негде.
 */
export const CareerVacancyCard = memo(
  ({
    title,
    companyName,
    location,
    workMode,
    seniority,
    employmentType,
    salary,
    description,
    directionTitles,
    skillNames,
    applyUrl,
    expiresAt,
    interaction,
    onSave,
    onOpenDetails,
    actionLoading,
  }: ICareerVacancyCard) => {
    const { t } = useTranslation();

    // `applied` — историческая самоотметка: кнопки для неё больше нет, но у
    // тех, кто успел её поставить, вакансия обязана остаться в избранном.
    const isSaved = interaction === 'saved' || interaction === 'applied';

    const workModeLabel = workMode
      ? t(`careerProfile.workMode.${workMode}`, { defaultValue: workMode })
      : '';
    const seniorityLabel = seniority
      ? t(`careerProfile.seniority.${seniority}`, { defaultValue: seniority })
      : '';
    const employmentLabel = employmentType
      ? t(`careerCenter.vacancies.employment.${employmentType}`, { defaultValue: employmentType })
      : '';

    const skillChips = skillNames.slice(0, MAX_SKILL_CHIPS);
    const extraSkills = skillNames.length - skillChips.length;

    const facts = [
      { key: 'location', icon: IconNames.VacancyLocation, value: location },
      { key: 'workMode', icon: IconNames.VacancyMode, value: workModeLabel },
      { key: 'seniority', icon: IconNames.VacancyLevel, value: seniorityLabel },
      { key: 'employment', icon: IconNames.VacancyEmployment, value: employmentLabel },
      { key: 'salary', icon: IconNames.VacancySalary, value: salary },
      {
        key: 'expires',
        icon: IconNames.Calendar,
        value: expiresAt
          ? `${t('careerCenter.vacancies.expiresAt')}: ${parseDateOnly(expiresAt)}`
          : '',
      },
    ].filter((fact) => Boolean(fact.value));

    return (
      <Card>
        <CardHeader>
          <HeaderMain onClick={onOpenDetails} role="button" tabIndex={0}>
            <CardTitle>{title}</CardTitle>
            <CompanyLine>
              <Company>{companyName}</Company>
              {location ? <Location>· {location}</Location> : null}
            </CompanyLine>
          </HeaderMain>

          <SaveToggle
            type="button"
            saved={isSaved}
            disabled={actionLoading}
            onClick={onSave}
            title={`${t(
              isSaved ? 'careerCenter.vacancies.unsave' : 'careerCenter.vacancies.save',
            )}`}
            aria-pressed={isSaved}
            aria-label={`${t(
              isSaved ? 'careerCenter.vacancies.unsave' : 'careerCenter.vacancies.save',
            )}`}>
            <Icon
              name={isSaved ? IconNames.VacancyBookmarkFilled : IconNames.VacancyBookmark}
              width="18px"
              height="18px"
            />
          </SaveToggle>
        </CardHeader>

        {facts.length ? (
          <FactRow>
            {facts.map((fact) => (
              <Fact key={fact.key} accent={fact.key === 'salary'}>
                <Icon name={fact.icon} width="14px" height="14px" />
                {fact.value}
              </Fact>
            ))}
          </FactRow>
        ) : null}

        {description ? <Description>{description}</Description> : null}

        {directionTitles.length || skillChips.length ? (
          <TagRow>
            {directionTitles.map((direction) => (
              <DirectionTag key={`dir-${direction}`}>{direction}</DirectionTag>
            ))}
            {skillChips.map((skill) => (
              <SkillTag key={`skill-${skill}`}>{skill}</SkillTag>
            ))}
            {extraSkills > 0 ? (
              <SkillTag>{t('careerCenter.vacancies.moreSkills', { n: extraSkills })}</SkillTag>
            ) : null}
          </TagRow>
        ) : null}

        <Actions>
          <DetailsButton variant="text" size={ButtonSizes.sm} onClick={onOpenDetails}>
            {t('careerCenter.vacancies.details')}
          </DetailsButton>

          <OpenLink href={applyUrl} target="_blank" rel="noopener noreferrer">
            {t('careerCenter.vacancies.open')}
          </OpenLink>
        </Actions>
      </Card>
    );
  },
);

const Card = styled.div`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  width: 100%;
  padding: 20px;
  border: 1px solid ${Colors.system_gray};
  border-radius: 12px;
  background-color: ${Colors.white};

  ${tablet} {
    padding: 24px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const HeaderMain = styled.div`
  min-width: 0;
  cursor: pointer;
`;

const CardTitle = styled.h3`
  margin: 0 0 4px 0;
  ${fontService.getFont({ size: 18, lineHeight: 133, weight: FontWeight.bold })}
`;

const CompanyLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px;
`;

const Company = styled.span`
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}
`;

const Location = styled.span`
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
`;

/** ГЛАВНОЕ действие карточки: уводит на оригинал вакансии. */
const OpenLink = styled.a`
  flex-shrink: 0;
  padding: 10px 18px;
  border: 1px solid ${Colors.main};
  border-radius: 10px;
  background-color: ${Colors.main};
  color: ${Colors.white};
  white-space: nowrap;
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}

  &:hover {
    opacity: 0.9;
    color: ${Colors.white};
  }
`;

const FactRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
`;

/** Иконка + значение. Иконки на `currentColor`, поэтому меняют цвет вместе с чипом. */
const Fact = styled.span<{ accent?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 8px;
  background-color: ${Colors.bg_gray};
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.extraSmall, weight: FontWeight.medium })}

  ${({ accent }) =>
    accent &&
    css`
      background-color: ${Colors.marker_green};
      color: ${Colors.black};
    `}
`;

/** Закладка: единственное оставшееся действие-состояние, и оно ОТМЕНЯЕМО. */
const SaveToggle = styled.button<{ saved?: boolean }>`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 1px solid ${({ saved }) => (saved ? Colors.main : Colors.system_gray)};
  border-radius: 10px;
  background: none;
  cursor: pointer;
  color: ${({ saved }) => (saved ? Colors.main : Colors.gray_dark)};

  &:hover:not(:disabled) {
    border-color: ${Colors.main};
    color: ${Colors.main};
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

const Description = styled.p`
  margin: 16px 0 0 0;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}

  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
`;

/**
 * Направление отличаем РАМКОЙ, а не заливкой: на тёмной теме LearnIT
 * `main_light` даёт жёлтый фон, а `main_dark` — почти белый текст, и надпись
 * становилась нечитаемой (поймано на живом стенде).
 */
const DirectionTag = styled.span`
  padding: 4px 10px;
  border-radius: 12px;
  border: 1px solid ${Colors.main};
  color: ${Colors.main};
  ${fontService.text({ type: TextType.extraSmall, weight: FontWeight.medium })}
`;

const SkillTag = styled.span`
  padding: 4px 10px;
  border-radius: 12px;
  border: 1px solid ${Colors.system_gray};
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.extraSmall, weight: FontWeight.regular })}
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
`;

const DetailsButton = styled(Button)`
  width: auto;
`;
